import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import {
  CircularProgress,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  ColourPicker,
  Page,
  TagChip,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { SnackSaved } from '../../../components/v2/snacks/Saved';

import { UserTag } from '../../../models/UserTag';

const { DateTime } = require("luxon");

type Props = {}

export const CoachUserTagEdit: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();
  const queryClient = useQueryClient();
  const { state } = useLocation();
  
  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailConsent, setEmailConsent] = useState<undefined|{enabled: boolean; is_blocked: boolean; exist: boolean; lsia: boolean}>(undefined);
  const [consentAsked, setConsentAsked] = useState<string>();
  const [consentOverride, setConsentOverride] = useState<boolean>(false);
  const [showConsentByProxy, setShowConsentByProxy] = useState<boolean>(false);
  const [consentByProxyData, setConsentByProxyData] = useState<boolean>(false);
  const [consentByProxyTerms, setConsentByProxyTerms] = useState<boolean>(false);
  const [consentByProxyTermsNutri, setConsentByProxyTermsNutri] = useState<boolean>(false);
  const [isActualUser, setIsActualUser] = useState<number>();
  const [mainObject, setMainObject] = useState<UserTag>({});
  const [item, setItem] = useState<File>();
  const [itemUrl, setItemUrl] = useState<string>();
  const [hasNoEmail, setHasNoEmail] = useState<boolean>(false);

  const [errors, setErrors] = useState<{[z:string]: boolean}>({});

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["user_tag", (login?.practice_id || currentPractice?.id), objectId],
    queryFn: () =>
      fetch_one<UserTag>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'user_tag',
          id: objectId,
          fields: [
            'id', 'name', 'colour', 'bg_colour', 'extra_data'
          ],
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    enabled: !!(login?.practice_id || currentPractice?.id) && parseInt(objectId || '', 10) > 0,
  });







  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
      keepOpen?: boolean
    }) => {
      return fetch_one<UserTag>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setSavedOpen(true);
      queryClient.invalidateQueries({ queryKey: ["coach", "user_tags", (login?.practice_id || currentPractice?.id)] });
      navigate("/user_tags");
    },
  });





  let page_title;
  if (typeof((object || {}).id) === 'undefined') {
    page_title = t("general.titles.add", "Nieuwe") + " " + t("coach.models.user_tags.singular", "tag");
  } else {
    page_title = t("general.titles.edit", "Wijzig") + " " + (object || {}).name;
  }

  useEffect(() => {
    if (params.id !== 'new') objectRefetch();
  }, [params.id]);
  useEffect(() => {
    if (!!objectSuccess) setMainObject(object);
  }, [object]);

  return <Page
    title={page_title}
    titleCrumbs={[
      {
        title: t("coach.menu.users", "Cliënten"),
        path: "/users"
      }
    ]}
  >
    <Paper sx={{padding: 2}}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextInput
            id="name"
            caption={t("coach.models.user_tags.name")}
            placeholder={t("coach.models.user_tags.placeholders.name")}
            value={mainObject.name || ''}
            onChange={(e) => {
              setMainObject({
                ...mainObject,
                name: e
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColourPicker
            label={t("coach.models.user_tags.bg_colour")}
            value={mainObject?.bg_colour || theme.palette.primary.main}
            onChange={(v) => {
              setMainObject({
                ...mainObject,
                bg_colour: v
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColourPicker
            label={t("coach.models.user_tags.colour")}
            value={mainObject?.colour || theme.palette.tertiary?.main}
            onChange={(v) => {
              setMainObject({
                ...mainObject,
                colour: v
              });
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography>{t("coach.models.user_tags.preview_extended")}</Typography>
          <TagChip tag={mainObject} />
        </Grid>






        <Grid item xs={12} sx={{
          alignContent: 'baseline',
          textAlign: 'center',
          position: {
              xs: 'relative',
              md: 'sticky'
            },
          bottom: '-24px',
          backgroundColor: 'white',
          zIndex: 1201
        }}>
          <Divider sx={{marginTop: 1, marginBottom: 0}} />

          {!!mutationSave.isPending && <CircularProgress />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.save")}
            id='save'
            disabled={!mainObject?.name}
            contained
            onClick={(v) => {
              mutationSave.mutate({
                formData: {
                  object: 'user_tag',
                  ob: {
                    ...mainObject,
                    practice_id: (login?.practice_id || currentPractice?.id)
                  }
                }
              });
            }}
            sx={{marginBottom: 1, marginTop: 1}}
          />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.cancel")}
            id='cancel'
            text
            // contained
            onClick={(v) => {
              navigate("/user_tags");
            }}
            sx={{marginLeft: 1, marginBottom: 1, marginTop: 1}}
          />}
        </Grid>
      </Grid>
    </Paper>
    
    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />
  </Page>;
}
